import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Team, getTeamsSubject } from '../../../store/Team';
import { Permission } from '../../../store/GeneralStore';
import { hasPermissions } from '../../../store/User';
import { assignPermissionSet, unassignPermissionSet, usePermissionSetAssignments } from '../../../store/PermissionSets';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { Icon } from '../../Atoms/Icon';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';
import { assignmentForTeam, teamsOnSet } from './userAssignmentHelpers';

export interface SetTeamAssignmentsProps {
  permissionSetId: string;
}

interface MembersTableProps {
  members: Team[];
  canEdit: boolean;
  submitting: boolean;
  onRemove: (team: Team) => void;
}

const MembersTable = ({ members, canEdit, submitting, onRemove }: MembersTableProps) => {
  if (!members.length) return <Typography color="text.secondary">No teams are assigned to this set yet.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Team</TableCell>
          <TableCell>Account</TableCell>
          <TableCell align="right">Members</TableCell>
          {canEdit && <TableCell align="right">Remove</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((team) => (
          <TableRow key={team.id}>
            <TableCell>{team.name}</TableCell>
            <TableCell>{team.account_name ?? '—'}</TableCell>
            <TableCell align="right">{team.user_count ?? 0}</TableCell>
            {canEdit && (
              <TableCell align="right">
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    aria-label={`Remove ${team.name}`}
                    disabled={submitting}
                    onClick={() => onRemove(team)}>
                    <Icon type="trash" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const SetTeamAssignments = ({ permissionSetId }: SetTeamAssignmentsProps) => {
  const { setSnack } = useSnackbar();
  const { assignments, submitting } = usePermissionSetAssignments();
  const [teams, setTeams] = useState<Team[]>(getTeamsSubject().getValue());
  const [toAdd, setToAdd] = useState<Team[]>([]);
  const canEdit = hasPermissions([Permission.ASSIGN_TEAM]);

  useEffect(() => {
    const sub = getTeamsSubject().subscribe(setTeams);
    return () => sub.unsubscribe();
  }, []);

  const members = useMemo(() => teamsOnSet(teams, assignments, permissionSetId), [teams, assignments, permissionSetId]);
  const memberIds = useMemo(() => new Set(members.map((team) => team.id)), [members]);
  const candidates = useMemo(() => teams.filter((team) => !memberIds.has(team.id)), [teams, memberIds]);

  const handleAdd = useCallback(async () => {
    const outcomes = await Promise.all(
      toAdd.map(async (team) => ({
        team,
        error: (await assignPermissionSet({ permission_set_id: permissionSetId, team_id: Number(team.id) })).error
      }))
    );
    const failed = outcomes.filter((outcome) => outcome.error);
    if (failed.length)
      setSnack({
        message: `Could not assign: ${failed.map((outcome) => outcome.team.name).join(', ')}`,
        type: 'error',
        open: true
      });
    setToAdd(failed.map((outcome) => outcome.team));
  }, [toAdd, permissionSetId, setSnack]);

  const handleRemove = useCallback(
    async (team: Team) => {
      const assignment = assignmentForTeam(assignments, team.id);
      if (!assignment) return;
      const result = await unassignPermissionSet(assignment.id);
      if (result.error)
        setSnack({ message: extractApiErrorMessage(result.error, 'Could not unassign'), type: 'error', open: true });
    },
    [assignments, setSnack]
  );

  return (
    <Stack
      spacing={2}
      data-cy="set-team-assignments">
      <Typography variant="subtitle2">Team Assignments</Typography>
      {!canEdit && <Alert severity="info">You do not have permission to change team assignments.</Alert>}
      {canEdit && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center">
          <Autocomplete
            multiple
            className="w-full"
            options={candidates}
            value={toAdd}
            onChange={(_event, value) => setToAdd(value)}
            getOptionLabel={(team) => team.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add teams"
              />
            )}
          />
          <button
            type="button"
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            disabled={!toAdd.length || submitting}
            onClick={handleAdd}>
            Add
          </button>
        </Stack>
      )}
      <MembersTable
        members={members}
        canEdit={canEdit}
        submitting={submitting}
        onRemove={handleRemove}
      />
    </Stack>
  );
};
